import { useMemo } from 'react';
import type React from 'react';

import { Avatar, Card, CardBody, Text } from '@change/ds-core-ui';

import { useNow } from '@change-corgi/core-react/date';
import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';

/**
 * A full-width card playing back the supporter's just-submitted comment, with the comment text as
 * the visually dominant element and the author/timestamp de-emphasized at the bottom. This is a
 * distinct layout from the existing `YourVoiceText` (author-first, comment secondary) — that
 * component is shared with the unrelated, unmodified `AddSupporterVoiceThankYou` control-arm screen,
 * so this is a separate component rather than a restyle of it.
 */
export const CommentCard: React.FC = () => {
	const [{ supporterVoices, newSupporterVoiceId }] = usePetitionActionsPageState();
	const supporterVoice = useMemo(
		() => supporterVoices.find((candidate) => candidate.id === newSupporterVoiceId),
		[supporterVoices, newSupporterVoiceId],
	);
	const { localizeRelativeTime } = useI18n();
	const now = useNow();

	return (
		<Card className="w-full" elevation>
			<CardBody>
				<div className="flex flex-col gap-2">
					<Text
						as="p"
						variant="body"
						size="medium"
						data-testid="contact-decision-maker-comment-text"
						className="text-typography-strong text-left break-words"
						style={{ wordBreak: 'break-word' }}
					>
						{supporterVoice?.text}
					</Text>
					<div className="flex items-center gap-2">
						<Avatar alt="" size={24} src={supporterVoice?.user.photo?.userSmall.url} />
						<Text variant="body" size="small" className="text-typography-weak">
							{supporterVoice?.user.shortDisplayName}
						</Text>
						<Text variant="body" size="small" className="text-typography-weak">
							•
						</Text>
						<Text variant="body" size="x-small" className="text-typography-weak">
							{localizeRelativeTime(now, now.valueOf() - 1_000)}
						</Text>
					</div>
				</div>
			</CardBody>
		</Card>
	);
};
